import { createContext, useEffect, useState } from 'react';
import { Profiles, getProfiles } from 'application/repositories/profilesRepository';
import { getCMSHostname } from 'helpers/getCMSHostname';
import { useSession } from 'next-auth/react';
import { GetContactResult, getContact } from 'application/adapters/contacts/contactsAdapter';
import { Audiences, getAudiences } from 'application/repositories/AudiencesRepository';
import { useImpersonateId } from 'application/hooks/useImpersonateId';
import logger from 'helpers/logger';

export const ProfileContext = createContext<{
	profile: Profiles;
	branches: GetContactResult;
	audiences: Audiences;
	isReady?: boolean;
	audiencesFailed?: boolean;
}>(null);

export const ProfileProvider = ({ children }) => {
	const [profile, setProfile] = useState<Profiles>(null);
	const [branches, setBranches] = useState<GetContactResult>(null);
	const [audiences, setAudiences] = useState<Audiences>(null);
	const [isReady, setIsReady] = useState(false);
	const [audiencesFailed, setAudiencesFailed] = useState(false);
	const impersonateId = useImpersonateId();
	const { status } = useSession();

	useEffect(() => {
		// The effect re-runs when impersonateId changes. Without this guard a slower earlier
		// request can resolve after a newer one and leave the previous member's audiences in
		// state, with isReady true and audiencesFailed false - the worst combination, because
		// consumers then filter confidently against the wrong data.
		let ignore = false;

		if (status === 'authenticated') {
			setIsReady(false);
			setAudiencesFailed(false);

			getProfiles(getCMSHostname())
				.then((profile) => {
					if (ignore) return;
					setProfile(profile);

					return getAudiences(getCMSHostname(), impersonateId).then((auds) => {
						if (ignore) return;
						setAudiences(auds);
					});
				})
				.catch((error) => {
					if (ignore) return;
					// Without this, a rejection left isReady false for the life of the page - audience
					// dependent modules then either rendered unfiltered or stayed blank permanently.
					// audiencesFailed keeps "we could not find out" distinct from "the user is in no
					// audiences", so consumers drop audience-gated items instead of failing open.
					logger.error('Failed to load profile or audiences: ' + error);
					setAudiences(null);
					setAudiencesFailed(true);
				})
				.finally(() => {
					if (ignore) return;
					setIsReady(true);
				});
		} else if (status === 'unauthenticated') {
			setProfile(null);
			setAudiences(null);
			setAudiencesFailed(false);
			setIsReady(true);
		}

		return () => {
			ignore = true;
		};
	}, [status, impersonateId]);

	useEffect(() => {
		if (status === 'authenticated') {
			getContact(getCMSHostname())
				.then((response) => {
					setBranches(response.data);
				})
				.catch((error) => {
					logger.error('Failed to load branches: ' + error);
				});
		} else if (status === 'unauthenticated') {
			setBranches(null);
		}
	}, [status]);

	const value = { profile, branches, audiences, isReady, audiencesFailed };

	return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
